// The patient's writes in an open thread, as pure helpers with injected
// collaborators (the api object, the auth). No id is minted here and nothing is
// imported from the HTTP layer, so each rule is tested without a network.

// The server accepts a clientMessageId only in this shape and refuses the whole
// send otherwise (messaging/contract/requestParsers.js).
export const CLIENT_MESSAGE_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/;

const GENERIC_DELETE_FAILURE = "The message could not be deleted.";

// One message to send (`POST /patient-app/messaging/conversations/:id/messages`,
// through the outbox, outbox.js). The sender is the verified session, so no
// tenant, location, sender or timestamp is ever sent; `clientMessageId`,
// minted once, is the request's Idempotency-Key.
export function buildPatientSend({conversationId, text, attachment, newId}) {
  if (typeof conversationId !== "string" || conversationId.length === 0) {
    throw new Error("A send needs the server conversation id");
  }
  const clientMessageId = newId();
  if (typeof clientMessageId !== "string" || !CLIENT_MESSAGE_ID_PATTERN.test(clientMessageId)) {
    throw new Error("A send needs a valid clientMessageId");
  }
  const payload = {
    conversationId,
    clientMessageId,
    messageType: attachment ? "file" : "text",
    message: text,
  };
  if (attachment) payload.attachment = attachment;
  return payload;
}

// A draft becomes a server conversation on the first send. The server owns the
// pair id: it returns the existing direct conversation or creates one, and the
// caller continues with the returned id. A clinic that does not let patients
// start conversations answers 403 PATIENT_INITIATE_DISABLED, which is a state of
// the thread, not an error.
export async function ensureServerConversation({conversation, auth, api}) {
  if (conversation?.conversationId) return {conversation, created: false};
  const target = conversation?.draftTarget;
  if (!target?.kind || !target?.id) {
    throw new Error("A draft conversation needs a draftTarget");
  }
  try {
    const result = await api.startDirect(auth, {kind: target.kind, id: target.id});
    return {conversation: result.conversation, created: result.created};
  } catch (err) {
    if (err?.code === "PATIENT_INITIATE_DISABLED") return {blocked: true};
    throw err;
  }
}

// Delete goes through REST (the socket delete answers MOVED_TO_REST). The
// result carries the server's reason, so a refusal is never silent.
export async function deletePatientMessage({auth, api, conversationId, messageId}) {
  try {
    await api.deleteMessage(auth, conversationId, messageId);
    return {ok: true};
  } catch (err) {
    return {ok: false, message: err?.message || GENERIC_DELETE_FAILURE};
  }
}
